
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Edit, Trash2, Eye, Plus, MapPin, CheckCircle, XCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import MultiStepPropertyForm from "./property-form/MultiStepPropertyForm";

interface PropertyManagementProps {
  properties: any[];
  onPropertiesChange: () => void;
  subscription?: any;
  isActive?: boolean;
}

const PropertyManagement = ({ properties, onPropertiesChange, subscription, isActive }: PropertyManagementProps) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProperty, setEditingProperty] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const handleDeleteProperty = async (propertyId: string) => {
    if (!confirm('هل أنت متأكد من حذف هذا العقار؟')) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from("properties")
        .delete()
        .eq("id", propertyId);

      if (error) throw error;

      toast({
        title: "نجح العملية",
        description: "تم حذف العقار بنجاح",
        variant: "default"
      });

      onPropertiesChange();
    } catch (error: any) {
      console.error('خطأ في حذف العقار:', error);
      toast({
        title: "خطأ",
        description: error.message || "حدث خطأ أثناء حذف العقار",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (propertyId: string, currentStatus: boolean) => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from("properties")
        .update({ is_active: !currentStatus })
        .eq("id", propertyId);

      if (error) throw error;

      toast({
        title: "نجح العملية",
        description: `تم ${!currentStatus ? 'تفعيل' : 'إلغاء تفعيل'} العقار بنجاح`,
        variant: "default"
      });

      onPropertiesChange();
    } catch (error: any) {
      console.error('خطأ في تحديث حالة العقار:', error);
      toast({
        title: "خطأ",
        description: error.message || "حدث خطأ أثناء تحديث حالة العقار",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleOccupied = async (propertyId: string, currentOccupied: boolean) => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from("properties")
        .update({ occupied: !currentOccupied })
        .eq("id", propertyId);

      if (error) throw error;

      toast({
        title: "نجح العملية",
        description: `تم تحديث حالة الإشغال إلى ${!currentOccupied ? 'مؤجر' : 'متاح'}`,
        variant: "default"
      });

      onPropertiesChange();
    } catch (error: any) {
      console.error('خطأ في تحديث حالة الإشغال:', error);
      toast({
        title: "خطأ",
        description: error.message || "حدث خطأ أثناء تحديث حالة الإشغال",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = () => {
    setShowAddForm(false);
    setEditingProperty(null);
    onPropertiesChange();
    
    toast({
      title: "نجح العملية",
      description: editingProperty ? "تم تحديث العقار بنجاح" : "تم إضافة العقار بنجاح وسيظهر في صفحة العقارات",
      variant: "default"
    });
  };

  const activeProperties = properties.filter(p => p.is_active);
  const inactiveProperties = properties.filter(p => !p.is_active);

  return (
    <div className="space-y-6">
      {/* إضافة عقار جديد */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-right">إدارة العقارات</CardTitle>
            <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
              <DialogTrigger asChild>
                <Button 
                  className="text-right"
                  disabled={!isActive || !subscription}
                  onClick={(e) => {
                    if (!isActive) {
                      e.preventDefault();
                      toast({
                        title: "حسابك غير مفعل",
                        description: "يرجى التواصل مع الإدارة لتفعيل حسابك أولاً",
                        variant: "destructive"
                      });
                    } else if (!subscription) {
                      e.preventDefault();
                      toast({
                        title: "لا يوجد اشتراك نشط",
                        description: "يرجى التواصل مع الإدارة لتفعيل اشتراكك وتحديد الباقة المناسبة لإضافة العقارات",
                        variant: "destructive"
                      });
                    }
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  إضافة عقار جديد
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-right">إضافة عقار جديد</DialogTitle>
                </DialogHeader>
                <MultiStepPropertyForm 
                  afterSubmit={handleFormSubmit}
                  onCancel={() => setShowAddForm(false)}
                />
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {!isActive ? (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <p className="text-orange-800 text-sm text-right">
                ⚠️ <strong>تنبيه:</strong> حسابك غير مفعل حالياً. يرجى التواصل مع الإدارة لتفعيل حسابك قبل إضافة العقارات.
              </p>
            </div>
          ) : !subscription ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 text-sm text-right">
                ⚠️ <strong>تنبيه:</strong> لا يوجد اشتراك نشط. لإضافة عقارات، يرجى التواصل مع الإدارة لتفعيل اشتراكك وتحديد الباقة المناسبة.
              </p>
            </div>
          ) : (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-blue-800 text-sm text-right">
                💡 <strong>ملاحظة:</strong> العقارات الجديدة تكون نشطة بشكل افتراضي وستظهر في صفحة العقارات العامة فوراً. لديك باقة {subscription.plan_type === 'basic' ? 'أساسية' : subscription.plan_type === 'standard' ? 'متوسطة' : 'متقدمة'} ({subscription.properties_limit} عقار).
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* قائمة العقارات */}
      <Tabs defaultValue="active" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="active" className="text-right">
            العقارات النشطة ({activeProperties.length})
          </TabsTrigger>
          <TabsTrigger value="inactive" className="text-right">
            العقارات غير النشطة ({inactiveProperties.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="space-y-4 mt-6">
          {activeProperties.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <p className="text-gray-600 mb-4">لا توجد عقارات نشطة</p>
                <Button onClick={() => setShowAddForm(true)}>
                  إضافة العقار الأول
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {activeProperties.map((property) => (
                <PropertyCard 
                  key={property.id}
                  property={property}
                  onEdit={setEditingProperty}
                  onDelete={handleDeleteProperty}
                  onToggleStatus={handleToggleStatus}
                  onToggleOccupied={handleToggleOccupied}
                  loading={loading}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="inactive" className="space-y-4 mt-6">
          {inactiveProperties.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <p className="text-gray-600">لا توجد عقارات غير نشطة</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {inactiveProperties.map((property) => (
                <PropertyCard 
                  key={property.id}
                  property={property}
                  onEdit={setEditingProperty}
                  onDelete={handleDeleteProperty}
                  onToggleStatus={handleToggleStatus}
                  onToggleOccupied={handleToggleOccupied}
                  loading={loading}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* نموذج التعديل */}
      {editingProperty && (
        <Dialog open={!!editingProperty} onOpenChange={() => setEditingProperty(null)}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-right">تعديل العقار</DialogTitle>
            </DialogHeader>
            <MultiStepPropertyForm 
              editingProperty={editingProperty}
              afterSubmit={handleFormSubmit}
              onCancel={() => setEditingProperty(null)}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

const PropertyCard = ({ property, onEdit, onDelete, onToggleStatus, onToggleOccupied, loading }: any) => {
  const [mainImage, setMainImage] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(true);

  React.useEffect(() => {
    fetchMainImage();
  }, [property.id]);

  const fetchMainImage = async () => {
    try {
      setImageLoading(true);
      console.log('OwnerPropertyCard: Fetching image for property:', property.id);

      const { data: images, error } = await supabase
        .from('property_images')
        .select('*')
        .eq('property_id', property.id)
        .order('image_order', { ascending: true });

      if (error) {
        console.error('OwnerPropertyCard: Error fetching property images:', error);
        setMainImage(null);
        return;
      }

      console.log('OwnerPropertyCard: Found images for property:', property.id, images);

      if (images && images.length > 0) {
        // البحث عن أول صورة لها storage_path صالح
        let mainImageData = images.find(img => img.storage_path && img.storage_path.trim() !== '');
        
        // إذا لم توجد صورة بـ storage_path، ابحث عن الصورة الرئيسية
        if (!mainImageData) {
          mainImageData = images.find(img => img.is_main);
        }
        
        // إذا لم توجد صورة رئيسية، استخدم أول صورة
        if (!mainImageData) {
          mainImageData = images[0];
        }

        let imageUrl: string | null = null;

        // إذا كان هناك storage_path صالح، استخدمه للحصول على الرابط
        if (mainImageData.storage_path && mainImageData.storage_path.trim() !== '') {
          const { data } = supabase.storage
            .from('property-images')
            .getPublicUrl(mainImageData.storage_path);
          
          imageUrl = data.publicUrl;
          console.log('OwnerPropertyCard: Using storage URL:', imageUrl);
        } 
        // إذا كان هناك URL مباشر وليس blob، استخدمه
        else if (mainImageData.url && !mainImageData.url.startsWith('blob:')) {
          imageUrl = mainImageData.url;
          console.log('OwnerPropertyCard: Using direct URL:', imageUrl);
        }

        setMainImage(imageUrl);
      } else {
        console.log('OwnerPropertyCard: No images found for property:', property.id);
        setMainImage(null);
      }
    } catch (error) {
      console.error('OwnerPropertyCard: Error in fetchMainImage:', error);
      setMainImage(null);
    } finally {
      setImageLoading(false);
    }
  };

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <div className="relative">
        {/* صورة العقار */}
        <div className="h-48 bg-gray-200 flex items-center justify-center overflow-hidden">
          {imageLoading ? (
            <div className="animate-pulse bg-gray-300 w-full h-full flex items-center justify-center">
              <Eye className="h-12 w-12 text-gray-400" />
            </div>
          ) : mainImage ? (
            <img
              src={mainImage}
              alt={property.title}
              className="w-full h-full object-cover"
              onLoad={() => console.log('OwnerPropertyCard: Image loaded successfully:', mainImage)}
              onError={(e) => {
                console.error('OwnerPropertyCard: Image failed to load:', mainImage);
                setMainImage(null);
              }}
            />
          ) : (
            <div className="text-center text-gray-500">
              <Eye className="h-12 w-12 mx-auto mb-2" />
              <p className="text-sm">صورة العقار</p>
            </div>
          )}
        </div>

        {/* شارات الحالة */}
        <div className="absolute top-2 left-2 flex gap-2">
          <Badge variant={property.is_active ? "default" : "secondary"}>
            {property.is_active ? "نشط" : "غير نشط"}
          </Badge>
          <Badge variant={property.occupied ? "destructive" : "default"} className={property.occupied ? "bg-orange-600" : "bg-green-600"}>
            {property.occupied ? "مؤجر" : "متاح"}
          </Badge>
        </div>
        
        {/* عدد المشاهدات */}
        <div className="absolute top-2 right-2 flex items-center bg-black/50 text-white px-2 py-1 rounded text-sm">
          <Eye className="h-4 w-4 ml-1" />
          {property.views || 0}
        </div>
      </div>

      <CardContent className="p-4">
        <CardTitle className="text-right text-lg mb-2">{property.title}</CardTitle>
        
        <div className="space-y-4">
          <div className="flex items-center text-gray-600 text-right">
            <MapPin className="h-4 w-4 ml-2" />
            <span>{property.address}</span>
          </div>

          <div className="text-right">
            <span className="text-xl font-bold text-green-600">
              {property.monthly_price} ر.ع
            </span>
            <span className="text-gray-500 text-sm mr-1">/ شهرياً</span>
          </div>

          <div className="grid grid-cols-3 gap-2 text-center text-sm">
            <div>
              <p className="text-gray-500">غرف النوم</p>
              <p className="font-semibold">{property.bedrooms}</p>
            </div>
            <div>
              <p className="text-gray-500">الحمامات</p>
              <p className="font-semibold">{property.bathrooms}</p>
            </div>
            <div>
              <p className="text-gray-500">المساحة</p>
              <p className="font-semibold">{property.area} م²</p>
            </div>
          </div>

          {/* حالة الإيجار مع Switch */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="text-right">
              <p className="font-medium text-sm">حالة الإيجار</p>
              <p className="text-xs text-gray-600">
                {property.occupied ? "العقار مؤجر حالياً" : "العقار متاح للإيجار"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">متاح</span>
              <Switch
                checked={property.occupied}
                onCheckedChange={() => onToggleOccupied(property.id, property.occupied)}
                disabled={loading}
              />
              <span className="text-sm text-gray-600">مؤجر</span>
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEdit(property)}
              disabled={loading}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onToggleStatus(property.id, property.is_active)}
              disabled={loading}
            >
              {property.is_active ? 'إلغاء تفعيل' : 'تفعيل'}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => onDelete(property.id)}
              disabled={loading}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PropertyManagement;
